import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Card,
  CardMedia,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  AutoAwesome,
  Close,
  CheckCircle,
  PhotoLibrary,
  Add,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { TASK_VALUES } from '../constants/tasks';
import timezoneService from '../services/timezoneService';
import { format } from 'date-fns-tz';

interface Screenshot {
  id: string;
  timestamp: string;
  thumbnail: string;
  fullImage: string;
  projectName?: string;
  activity: string;
  confidence: number;
  duration: number;
  productivity: string;
  tags: string[];
  detectedApplications: string[];
  workType: string;
  productivityLevel: string;
  analysis: string;
  aiAnalysis?: {
    metadata?: {
      context?: {
        project?: string;
        activity?: string;
      };
    };
  };
}

interface Project {
  id: string;
  name: string;
  description: string;
  categoryType: string;
  categories?: string[];
  clientId?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

interface Client {
  id: string;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  address?: string;
}

interface SmartScreenshotTimeEntryProps {
  onTimeEntryCreated?: () => void;
  selectedDate?: string; // YYYY-MM-DD format
}

export const SmartScreenshotTimeEntry: React.FC<SmartScreenshotTimeEntryProps> = ({
  onTimeEntryCreated,
  selectedDate
}) => {
  const [open, setOpen] = useState(false);
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedScreenshots, setSelectedScreenshots] = useState<Set<string>>(new Set());
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load screenshots and projects when dialog opens
  useEffect(() => {
    if (open) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load projects and clients
      const [projectsData, clientsData] = await Promise.all([
        apiService.getProjects(),
        apiService.getClients()
      ]);
      setProjects(projectsData);
      setClients(clientsData);

      // Load screenshots for the selected date (or today if no date specified)
      const targetDate = selectedDate || timezoneService.getCurrentUserDate(); // YYYY-MM-DD in user timezone
      
      // Fetch ALL screenshots (backend doesn't filter by date reliably)
      const allScreenshotsResponse = await apiService.getScreenshots({ limit: 999 } as any);
      const allScreenshotsData = allScreenshotsResponse.data;
      
      // FRONTEND TIMEZONE FILTERING: Filter to only screenshots from target date
      const screenshotsData = allScreenshotsData.filter((ss: any) => {
        const zonedDate = timezoneService.convertUTCToUserTime(ss.timestamp);
        const dateKey = format(zonedDate, 'yyyy-MM-dd');
        return dateKey === targetDate;
      });
      
      console.log(`📸 SmartScreenshotTimeEntry: Filtered ${screenshotsData.length}/${allScreenshotsData.length} screenshots for ${targetDate} in user timezone`);
      setScreenshots(screenshotsData);
      
      if (screenshotsData.length === 0) {
        setError(`No screenshots found for ${targetDate}. Make sure you have screenshot tracking enabled and have taken some screenshots.`);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load screenshots, projects, and clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshotToggle = (screenshotId: string) => {
    const newSelected = new Set(selectedScreenshots);
    if (newSelected.has(screenshotId)) {
      newSelected.delete(screenshotId);
    } else {
      newSelected.add(screenshotId);
    }
    setSelectedScreenshots(newSelected);
  };

  // Handle client selection change (cascade to reset project and task)
  const handleClientChange = (clientId: string) => {
    setSelectedClient(clientId);
    setSelectedProject(''); // Reset project when client changes
    setSelectedTask(''); // Reset task when client changes
  };

  // Handle project selection change (cascade to reset task)
  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    setSelectedTask(''); // Reset task when project changes
  };

  // Get projects filtered by selected client
  const getFilteredProjects = (): Project[] => {
    if (!selectedClient) {
      return projects; // Show all projects if no client selected
    }
    return projects.filter(project => project.clientId === selectedClient);
  };

  // Get available tasks for selected project
  const getAvailableTasks = (): string[] => {
    if (!selectedProject) {
      return TASK_VALUES; // Use centralized default tasks when no project selected
    }
    
    const project = projects.find(p => p.id === selectedProject);
    if (!project) {
      return []; // Return empty array if project not found
    }
    
    // Start with the project's primary categoryType
    const tasks = [project.categoryType];
    
    // Add additional categories if they exist
    if (project.categories && project.categories.length > 0) {
      project.categories.forEach(category => {
        if (category && category.trim() !== '' && !tasks.includes(category)) {
          tasks.push(category);
        }
      });
    }
    
    // Return ONLY the tasks specifically configured for this project
    return tasks.filter(task => task && task.trim() !== '');
  };

  const handleCreateTimeEntry = async () => {
    if (selectedScreenshots.size === 0) {
      setError('Please select at least one screenshot');
      return;
    }

    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Get selected screenshot data
      const selectedScreenshotData = screenshots.filter(s => selectedScreenshots.has(s.id));
      
      // Calculate total duration (5 minutes per screenshot as baseline)
      const totalMinutes = Math.max(selectedScreenshotData.length * 5, 15); // Minimum 15 minutes
      
      // Use the first screenshot's timestamp as start time (sort by UTC timestamp)
      const firstScreenshot = selectedScreenshotData.sort((a, b) => 
        // eslint-disable-next-line no-restricted-syntax
        new Date(a.timestamp).getTime() - // UTC comparison for sorting
        // eslint-disable-next-line no-restricted-syntax
        new Date(b.timestamp).getTime()
      )[0];
      
      // eslint-disable-next-line no-restricted-syntax
      const startTime = new Date(firstScreenshot.timestamp); // Parse UTC timestamp from API
      // eslint-disable-next-line no-restricted-syntax
      const endTime = new Date(startTime.getTime() + (totalMinutes * 60 * 1000)); // Calculate end time
      
      // Create description from screenshots
      const activities = selectedScreenshotData.map(s => s.activity).filter(Boolean);
      const uniqueActivities = Array.from(new Set(activities));
      const description = notes || `Work session based on ${selectedScreenshotData.length} screenshots: ${uniqueActivities.join(', ')}`;
      
      // Get detected applications
      const allApps = selectedScreenshotData.flatMap(s => s.detectedApplications || []);
      const uniqueApps = Array.from(new Set(allApps));
      
      const timeEntryData = {
        projectId: selectedProject,
        task: selectedTask || 'General',
        description: description,
        notes: notes || description, // Send notes to both description and notes fields
        aiDescription: `Generated from ${selectedScreenshotData.length} screenshots. Apps used: ${uniqueApps.join(', ')}`,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        duration: totalMinutes,
        screenshotIds: Array.from(selectedScreenshots) // Track which screenshots were used
      };

      await apiService.createTimeEntry(timeEntryData);
      
      // Reset state
      handleClose();
      
      // Notify parent component
      if (onTimeEntryCreated) {
        onTimeEntryCreated();
      }

      // Show success message
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      alert(`Time entry created successfully! Added ${timeStr} for ${selectedTask || 'General'} work.`);
      
    } catch (error) {
      console.error('Error creating time entry:', error);
      setError('Failed to create time entry. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedScreenshots(new Set());
    setSelectedClient('');
    setSelectedProject('');
    setSelectedTask('');
    setNotes('');
    setError(null);
  };

  const getProductivityColor = (productivity: string | undefined) => {
    if (!productivity) return '#95a5a6';
    
    switch (productivity.toLowerCase()) {
      case 'high': return '#27ae60';
      case 'medium': return '#f39c12';
      case 'low': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const formatTime = (timestamp: string) => {
    // Convert UTC timestamp to user timezone and format time
    const userTime = timezoneService.convertUTCToUserTime(timestamp);
    return timezoneService.formatUserTime(userTime, { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  // Get target date in YYYY-MM-DD format
  const targetDate = selectedDate || timezoneService.getCurrentUserDate();
  // Format for display - parse as local date, not UTC
  // eslint-disable-next-line no-restricted-syntax
  const displayDate = timezoneService.formatUserDate(new Date(targetDate + 'T00:00:00'), { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PhotoLibrary />}
        onClick={() => setOpen(true)}
        sx={{
          textTransform: 'none',
          borderColor: '#e67e22',
          color: '#e67e22',
          '&:hover': {
            backgroundColor: '#e67e22',
            color: 'white',
          },
        }}
      >
        Create from Screenshots
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '600px',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
          <AutoAwesome sx={{ color: '#e67e22' }} />
          Create Time Entry from Screenshots
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
            {displayDate}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Loading State */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#e67e22' }} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading screenshots...
                </Typography>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Screenshots Grid */}
            {!loading && screenshots.length > 0 && (
              <>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Select Screenshots ({selectedScreenshots.size} selected)
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                    gap: 2, 
                    maxHeight: '300px', 
                    overflowY: 'auto', 
                    pr: 1 
                  }}>
                    {screenshots.map((screenshot) => (
                      <Card 
                        key={screenshot.id}
                        sx={{ 
                          cursor: 'pointer',
                          border: selectedScreenshots.has(screenshot.id) ? '3px solid #27ae60' : '1px solid #e0e0e0',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: 3,
                          }
                        }}
                        onClick={() => handleScreenshotToggle(screenshot.id)}
                      >
                        {selectedScreenshots.has(screenshot.id) && (
                          <CheckCircle 
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8, 
                              color: '#27ae60',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              zIndex: 1
                            }} 
                          />
                        )}
                        <CardMedia
                          component="img"
                          height="120"
                          image={screenshot.thumbnail}
                          alt="Screenshot"
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1.5, pb: 1.5 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                            {formatTime(screenshot.timestamp)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                            {screenshot.activity || 'Unknown Activity'}
                          </Typography>
                          
                          {/* Project Name from AI analysis (prioritize AI matching over metadata) */}
                          {(screenshot.projectName || screenshot.aiAnalysis?.metadata?.context?.project) && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              color: '#1976d2', 
                              fontWeight: 'bold',
                              mb: 0.5,
                              fontSize: '0.65rem'
                            }}>
                              📁 {screenshot.projectName || screenshot.aiAnalysis?.metadata?.context?.project}
                            </Typography>
                          )}
                          
                          {/* Work Type as Task */}
                          {screenshot.workType && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              color: '#9c27b0', 
                              fontWeight: 'medium',
                              mb: 0.5,
                              fontSize: '0.65rem'
                            }}>
                              📋 {screenshot.workType}
                            </Typography>
                          )}
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={screenshot.productivity || 'medium'}
                              size="small"
                              sx={{
                                backgroundColor: getProductivityColor(screenshot.productivity),
                                color: 'white',
                                fontSize: '0.65rem',
                                height: '18px',
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                              {screenshot.confidence}%
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>

                <Divider />

                {/* Client, Project and Task Selection */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Time Entry Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Client Selection */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Client
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={selectedClient}
                          onChange={(e) => handleClientChange(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select a client (optional)</em>
                          </MenuItem>
                          {clients.map((client) => (
                            <MenuItem key={client.id} value={client.id}>
                              {client.name}{client.company ? ` - ${client.company}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Project Selection */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Project
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={selectedProject}
                          onChange={(e) => handleProjectChange(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select a project</em>
                          </MenuItem>
                          {getFilteredProjects().map((project) => (
                            <MenuItem key={project.id} value={project.id}>
                              {project.name}
                              {selectedClient && project.clientId !== selectedClient ? 
                                ` (${clients.find(c => c.id === project.clientId)?.name || 'No Client'})` : 
                                ''
                              }
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedClient && getFilteredProjects().length === 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                          No projects found for selected client. You can still select from all projects above.
                        </Typography>
                      )}
                    </Box>

                    {/* Task Selection */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Task
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={selectedTask}
                          onChange={(e) => setSelectedTask(e.target.value)}
                          displayEmpty
                          disabled={!selectedProject}
                        >
                          <MenuItem value="">
                            <em>{selectedProject ? 'Select a task' : 'Select project first'}</em>
                          </MenuItem>
                          {getAvailableTasks().map((task) => (
                            <MenuItem key={task} value={task}>
                              {task}
                              {selectedProject && projects.find(p => p.id === selectedProject)?.categoryType === task ? 
                                ' (Primary)' : ''
                              }
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedProject && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem' }}>
                          Tasks are based on the selected project's configuration.
                          Primary task: <strong>{projects.find(p => p.id === selectedProject)?.categoryType || 'General'}</strong>
                        </Typography>
                      )}
                    </Box>

                    <TextField
                      fullWidth
                      label="Notes (optional)"
                      placeholder="Add notes about this work session..."
                      variant="outlined"
                      multiline
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Box>
                </Box>

                {/* Summary */}
                {selectedScreenshots.size > 0 && (
                  <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Time Entry Summary:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      • {selectedScreenshots.size} screenshots selected
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      • Estimated duration: {Math.max(selectedScreenshots.size * 5, 15)} minutes
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      • Time range: {screenshots.filter(s => selectedScreenshots.has(s.id)).length > 0 ? 
                        (() => {
                          const selected = screenshots.filter(s => selectedScreenshots.has(s.id))
                            .sort((a, b) => 
                              // eslint-disable-next-line no-restricted-syntax
                              new Date(a.timestamp).getTime() - // UTC comparison for sorting
                              // eslint-disable-next-line no-restricted-syntax
                              new Date(b.timestamp).getTime()
                            );
                          const start = formatTime(selected[0].timestamp);
                          const end = selected.length > 1 ? formatTime(selected[selected.length - 1].timestamp) : start;
                          return `${start} - ${end}`;
                        })() : 'N/A'
                      }
                    </Typography>
                  </Paper>
                )}
              </>
            )}

            {/* No Screenshots State */}
            {!loading && screenshots.length === 0 && !error && (
              <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                <PhotoLibrary sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                  No Screenshots Found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  No screenshots were found for {displayDate}.
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Make sure screenshot tracking is enabled in AI Settings and try taking some screenshots of your work.
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateTimeEntry}
            disabled={creating || selectedScreenshots.size === 0 || !selectedProject}
            sx={{
              backgroundColor: '#27ae60',
              '&:hover': {
                backgroundColor: '#219a52',
              },
              textTransform: 'none',
              px: 3,
            }}
          >
            {creating ? 'Creating...' : `Create Time Entry (${Math.max(selectedScreenshots.size * 5, 15)}m)`}
          </Button>
          <Button
            onClick={handleClose}
            sx={{ textTransform: 'none' }}
            startIcon={<Close />}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
